import { revealForm } from '../components/reveal-form.js';
import { createRemindersView } from './reminders.js';
import { ApiError, RequestAbortError } from '../../api/client.js';
import { h, toast, confirmModal, formModal, type View } from '../components/dom.js';
import { renderHeader } from '../components/header.js';
import type { SessionController } from '../../session/controller.js';

type Memory = { id: string; text: string; category: string; createdAt: string; updatedAt: string };

const CATEGORIES = ['general', 'preferencias', 'familia', 'salud', 'casa'] as const;
type Category = typeof CATEGORIES[number];

export function createMemoriaView(controller: SessionController): View {
  let active = true;
  const lifetime = new AbortController();
  const root = h('section', { class: 'view view-memoria', id: 'view-memoria' });
  root.appendChild(renderHeader('memoria'));
  const body = h('div', { class: 'section-body' });
  root.appendChild(body);
  const refreshButton = h('button', { class: 'btn ghost refresh', type: 'button', hidden: true }, ['Reintentar']);
  refreshButton.addEventListener('click', () => { void refresh(); reminders.update?.({}); });
  body.appendChild(refreshButton);

  // Editor card
  const editor = h('section', { class: 'card' });
  editor.append(
    h('h2', { class: 'card-title' }, ['Un recuerdo para ASIS']),
    h('p', { class: 'card-sub' }, ['Guarda tus preferencias aquí o díselo a ASIS: «Recuerda que prefiero respuestas cortas». Puedes activar su uso desde Mi cuenta.']),
  );
  const form = h('form', { class: 'form-stack' });
  const textarea = h('textarea', { class: 'textarea', rows: '3', placeholder: 'Ej. Prefiero que me llamen por el diminutivo.', 'aria-label': 'Texto de la memoria', maxlength: '2000' });
  const catSelect = h('select', { class: 'select', 'aria-label': 'Categoría' });
  for (const c of CATEGORIES) {
    const opt = h('option', { value: c }, [c]);
    catSelect.appendChild(opt);
  }
  const row = h('div', { class: 'form-row' });
  const saveBtn = h('button', { type: 'submit', class: 'btn primary' }, ['Guardar']);
  row.append(catSelect, saveBtn);
  form.append(textarea, row);
  editor.appendChild(form);
  const memoryReveal = revealForm(editor, 'Añadir recuerdo', 'memory-editor');
  const toolbar = h('div', { class: 'toolbar' }, [memoryReveal.button, refreshButton]);
  memoryReveal.button.className = 'btn primary';
  body.append(toolbar, editor);

  // List card
  const listCard = h('section', { class: 'card' });
  listCard.append(h('h2', { class: 'card-title' }, ['Tus recuerdos']));
  const list = h('ul', { class: 'memory-list' });
  listCard.appendChild(list);
  body.appendChild(listCard);

  const api = controller.getApi();
  const reminders = createRemindersView(api, { embedded: true });
  body.appendChild(reminders.el);
  let memories: Memory[] = [];
  let loading = false;

  async function refresh(): Promise<void> {
    if (loading) return;
    loading = true;
    list.replaceChildren(h('li', { class: 'muted' }, ['Cargando…']));
    try {
      const r = await api.listMemories(lifetime.signal);
      if (!active) return;
      if (!active) return;
      refreshButton.hidden = true;
      memories = r.memories;
      renderList();
    } catch (err) {
      if (!active || err instanceof RequestAbortError) return;
      refreshButton.hidden = false;
      list.replaceChildren(h('li', { class: 'muted' }, [`No se pudo cargar (${(err as Error).message})`]));
    } finally { loading = false; }
  }

  function renderList(): void {
    list.replaceChildren();
    if (memories.length === 0) { list.appendChild(h('li', { class: 'empty' }, [h('h3', {}, ['Un lugar para lo importante']), h('p', {}, ['Aún no hay recuerdos. Dile a ASIS qué te gustaría que tuviera presente, o añade un recuerdo.']), h('a', { class: 'btn ghost', href: '#/conversar' }, ['Hablar con ASIS'])])); return; }
    for (const m of memories) list.appendChild(renderRow(m));
  }

  function renderRow(m: Memory): HTMLElement {
    const li = h('li', { class: 'memory-row' });
    const text = h('div', { class: 'memory-text' }, [m.text]);
    const meta = h('div', { class: 'memory-meta' }, [`${m.category || 'general'} · ${new Date(m.updatedAt).toLocaleString()}`]);
    const actions = h('div', { class: 'memory-actions' });
    const edit = h('button', { class: 'btn ghost small', type: 'button' }, ['Editar']);
    const del = h('button', { class: 'btn ghost small danger', type: 'button' }, ['Borrar']);
    edit.addEventListener('click', () => { void doEdit(m); });
    del.addEventListener('click', () => { void doDelete(m); });
    actions.append(edit, del);
    li.append(text, meta, actions);
    return li;
  }

  async function doEdit(m: Memory): Promise<void> {
    await formModal({ title: 'Editar recuerdo', fields: [{ name: 'text', label: 'Recuerdo', value: m.text, multiline: true }, { name: 'category', label: 'Categoría', value: m.category || 'general' }], onSave: async values => {
      if (!values.text) throw new Error('El recuerdo no puede estar vacío.');
      const r = await api.patchMemory(m.id, { text: values.text, category: values.category || 'general' });
      if (!active) return;
      if (!active) return;
      if (!active) return;
      memories = memories.map(x => x.id === m.id ? r.memory : x);
      renderList(); toast('Recuerdo actualizado', 'ok');
    } });
  }

  async function doDelete(m: Memory): Promise<void> {
    const ok = await confirmModal({ title: 'Borrar recuerdo', body: 'Este recuerdo se eliminará permanentemente.', confirmLabel: 'Borrar', danger: true });
    if (!ok) return;
    try {
      await api.deleteMemory(m.id);
      if (!active) return;
      memories = memories.filter((x) => x.id !== m.id);
      renderList();
      toast('Memoria borrada', 'ok');
    } catch (err) {
      if (!active || err instanceof RequestAbortError) return;
      toast(`No se pudo borrar: ${apiMessage(err)}`, 'error');
    }
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (saveBtn.disabled) return;
    const text = textarea.value.trim();
    if (text.length === 0) { toast('Escribe algo antes de guardar', 'error'); return; }
    const category = (catSelect.value || 'general') as Category;
    const savedText = textarea.value;
    saveBtn.disabled = true;
    try {
      const r = await api.createMemory({ text, category });
      if (!active) return;
      if (!active) return;
      memories = [r.memory, ...memories];
      textarea.value = ''; memoryReveal.close(); memoryReveal.button.focus();
      renderList();
      toast('Memoria guardada', 'ok');
    } catch (err) {
      if (!active || err instanceof RequestAbortError) return;
      // On failure keep the form contents so the user can retry without losing work.
      textarea.value = savedText;
      toast(`No se pudo guardar: ${apiMessage(err)}`, 'error');
    } finally {
      saveBtn.disabled = false;
    }
  });

  void refresh();
  return { el: root, destroy() { active = false; lifetime.abort(); reminders.destroy?.(); }, update() { void refresh(); reminders.update?.({}); } };
}

function apiMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return (err as Error).message ?? 'error';
}
